import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { FileText } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export const PdfThumbnail = ({ url, filename, className = "" }) => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(400 / base.width, 2);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        if (!cancelled) setStatus("done");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (status === "error") {
    return (
      <div
        className={`w-full h-full flex flex-col items-center justify-center bg-red-50 gap-2 p-3 ${className}`}
        data-testid="pdf-thumb-fallback"
      >
        <FileText className="w-9 h-9 text-red-500" />
        <span className="text-xs text-center text-muted-foreground line-clamp-2 break-all">
          {filename || "PDF document"}
        </span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-white" data-testid="pdf-thumb">
      <canvas ref={canvasRef} className="w-full h-full object-contain object-top" />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50">
          <FileText className="w-8 h-8 text-red-400 animate-pulse" />
        </div>
      )}
      <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-medium uppercase tracking-wide">
        PDF
      </span>
    </div>
  );
};
